import { useState, useEffect, useContext } from 'react';
import { Popup } from 'devextreme-react/popup';
import { OidcIdentityContext } from '@rsmus/react-auth';
import SelectBox from 'devextreme-react/select-box';
import { Button } from 'devextreme-react';
import { useAppDispatch } from 'store/app/hooks';
import { showErrorSnackbar } from 'cui/components/snackbar/Snackbar.slice';
import './ReportsClientPopUp.scss';
import { useNavigate } from 'react-router-dom';
import { 
    setReportClientDropDownData,
    setSelectedReportClient,
    setSelectedReportEngagement,
    setReportEngagementDropDownData
 } from 'cui/components/breadcrumb/breadcrumb.slice';
import { ErrorCodesUI } from 'constants/ErrorCodes';
import { selectedApplication } from 'store/app/applications.slice';
import { useInsightApi } from 'hooks/use-insight-api';

type ReportsClientPopUpProps = {
    clientsData?: { key: string; value: string }[];
    applicationId: string;
    applicationName: string;
    isVisible: boolean;
    onClose: () => void;
};

const ReportsClientPopUp = ({ clientsData, applicationId, applicationName, isVisible, onClose }: ReportsClientPopUpProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { user } = useContext(OidcIdentityContext);
    const [selectedClient, setSelectedClient] = useState<string | null>(null);
    const [selectedEngagement, setSelectedEngagement] = useState<string | null>(null);
    const [engagementsList, setEngagementsList] = useState<any>([]);
    const [internalClientsData, setInternalClientsData] = useState<{ key: string; value: string }[]>([]);
    const [isLoadingClients, setIsLoadingClients] = useState<boolean>(false);
    const [isLoadingEngagements, setIsLoadingEngagements] = useState<boolean>(false);
    const [hasNoEngagements, setHasNoEngagements] = useState<boolean>(false);
    const { getEngagementsForUserReports, getClientsForUserReports } = useInsightApi();

    useEffect(() => {
        const fetchClients = async () => {
            if (isVisible && !clientsData && applicationId) {
                setIsLoadingClients(true);
                try {
                    const data = await getClientsForUserReports(applicationId);
                    if (data && data.length > 0) {
                        setInternalClientsData(data.map((r: any) => ({
                            key: r.key,
                            value: r.value
                        })));
                    } else {
                        setInternalClientsData([]);
                        showErrorSnackbar(dispatch, `${ErrorCodesUI.NoClientsForApplication} - No clients available for this application.`);
                    }
                } catch (e) {
                    setInternalClientsData([]);
                    showErrorSnackbar(dispatch, `${ErrorCodesUI.NoClientsForApplication} - Error fetching clients.`);
                }
                setIsLoadingClients(false);
            }
        };
        fetchClients();
    }, [isVisible, applicationId, clientsData]);

    const activeClientsData = clientsData || internalClientsData;
    
    const isInternalUser = () => {
        const username = user?.profile?.preferred_username;
        return username?.toLowerCase().endsWith('rsmus.com') || false;
    };
    
    useEffect(() => {
        if (isVisible && !selectedClient && activeClientsData.length > 0) {
            if (activeClientsData.length === 1 || !isInternalUser()) {
                const firstClient = activeClientsData[0];
                populateEngagements(firstClient.key);
            }
        }
    }, [isVisible, activeClientsData]);
    
    const populateEngagements = async (clientId: string) => {
        setSelectedClient(clientId);
        setSelectedEngagement(null);
        setIsLoadingEngagements(true);
        setHasNoEngagements(false);
        try {
            const engagements = await getEngagementsForUserReports(applicationId, clientId);
            if(engagements.length > 0) {
			    setEngagementsList(engagements.map((engagement) => ({
                    key: engagement.key,
                    value: engagement.value
                })));
                setHasNoEngagements(false);
            }
            else {
                setEngagementsList([]);
                setHasNoEngagements(true);
                showErrorSnackbar(dispatch, `${ErrorCodesUI.NoEngagementsForClient} - You do not have any engagements assigned, please contact your administrator for support.`);
            }
        } finally {
            setIsLoadingEngagements(false);
        }
    };
    const handleConfirm = () => {
        if (!(selectedClient)) {
             showErrorSnackbar(dispatch, `${ErrorCodesUI.NoClientSelected} - Please select a client.`);
             return;
        }
        if (!(selectedEngagement)) {
             showErrorSnackbar(dispatch, `${ErrorCodesUI.NoEngagementSelected} - Please select an engagement.`);
             return;
        }
            dispatch((selectedApplication(applicationId)));
            dispatch(setReportClientDropDownData(activeClientsData.map((client) => ({
                id: client.key,
                value: client.value
            }))));
            dispatch(setSelectedReportClient(selectedClient));
            dispatch(setReportEngagementDropDownData(engagementsList.map((engagement: any) => ({
                id: engagement.key,
                value: engagement.value
            }))));
            dispatch(setSelectedReportEngagement(selectedEngagement));
            navigate(`${applicationId}/${selectedClient}/${selectedEngagement}`);
            onClose();
    };

    const onCancel = () => {
        setSelectedClient(null);
        setSelectedEngagement(null);
        setInternalClientsData([]);
        setEngagementsList([]);
        setIsLoadingEngagements(false);
        setHasNoEngagements(false);
        onClose();
    };

    return (
        <Popup
            visible={isVisible}
            onHiding={onCancel}
            showCloseButton={true}
            showTitle={true}
            title={`Select Client and Engagement${typeof applicationName === 'string' ? ` for ${applicationName}` : ''}`}
            width={400}
            height={250}
            dragEnabled={false}
        >
            <div>
                <div className="input-group">
                    <label className="text-label">Select Client:</label>
                    <SelectBox
                        dataSource={activeClientsData}
                        displayExpr="value"
                        valueExpr="key"
                        value={selectedClient}
                        onValueChange={(value) => populateEngagements(value)}
                        minSearchLength={3}
                        stylingMode="outlined"
                        placeholder={isLoadingClients ? "Loading clients..." : "Type min. 3 chars to search for a client"}
                        searchEnabled={true}
                        disabled={isLoadingClients || activeClientsData.length === 0}
                    />
                </div>
                <div className="input-group">
                    <label className="text-label">Select Engagement:</label>
                    <SelectBox
                        dataSource={selectedClient ? engagementsList : []}
                        displayExpr="value"
                        valueExpr="key"
                        value={selectedEngagement}
                        onValueChange={(value) => setSelectedEngagement(value)}
                        minSearchLength={3}
                        stylingMode="outlined"
                        placeholder={
                            isLoadingEngagements
                                ? "Loading engagements..."
                                : hasNoEngagements
                                ? "No engagements found. Please select another client."
                                : selectedClient
                                ? "Type min. 3 chars to search for an engagement"
                                : "Awaiting client selection"
                        }
                        searchEnabled={true}
                        disabled={!selectedClient || engagementsList.length === 0 || isLoadingEngagements}
                    />
                </div>
                <div className="popup-buttons">
                    <Button
                        text="Cancel"
                        type="normal"
                        stylingMode="contained"
                        onClick={onCancel}
                    />
                    <Button
                        text="Submit"
                        type="default"
                        stylingMode="contained"
                        onClick={handleConfirm}
                    />
                </div>
            </div>
        </Popup>
    );
};

export default ReportsClientPopUp;